/**
 * The Summary tab's charts: inline SVG, no library.
 *
 * Four forms and nothing else — horizontal bars for a list of choices, a
 * donut for a part-of-a-whole with a few parts, columns for a distribution
 * over a numeric range, and a line for counts over days. Every chart is a
 * picture of a table that is also on the page for a screen reader, and the
 * same rows are what "Copy as CSV" copies.
 *
 * Colours are the console's tokens: one series is the accent; the donut's
 * few slices use a fixed order of five hues checked for colour-vision
 * separation, with a neutral for "Other". Text is always ink, never the
 * series colour.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useToast } from '../components/Toast';
import { NARROW, matchesNow, useMediaQuery } from './media';

/** The donut's slice colours, in fixed order — never cycled past five. */
export const SLICE_COLOURS = ['#667eea', '#dd6b20', '#38a169', '#805ad5', '#0987a0'];
export const OTHER_COLOUR = '#a0aec0';
const ACCENT = 'var(--accent, #667eea)';
const TRACK = 'var(--well, #f7fafc)';
const INK = 'var(--ink-800, #2d3748)';
const MUTED = 'var(--ink-500, #718096)';
const GRID = 'var(--border-soft, #edf2f7)';

export type CsvRow = (string | number | null | undefined)[];

/** Rows to CSV text, quoted where a value would break a line or a column. */
export function toCsv(rows: CsvRow[]): string {
  const esc = (v: string | number | null | undefined) => {
    const t = v === null || v === undefined ? '' : String(v);
    return /[",\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  return rows.map((r) => r.map(esc).join(',')).join('\r\n');
}

/** The table a chart is a picture of — present, and read, but not seen. */
export function HiddenTable({ caption, header, rows }: { caption: string; header: string[]; rows: CsvRow[] }) {
  return (
    <table className="fs-sr-only" data-testid="chart-table">
      <caption>{caption}</caption>
      <thead><tr>{header.map((h, i) => <th key={i} scope="col">{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>{r.map((c, j) => j === 0 ? <th key={j} scope="row">{c ?? ''}</th> : <td key={j}>{c ?? ''}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

/** Copies the chart's rows as CSV; says so in a toast either way. */
export function CopyCsvButton({ header, rows, what }: { header: string[]; rows: CsvRow[]; what: string }) {
  const toast = useToast();
  async function copy() {
    try {
      await navigator.clipboard.writeText(toCsv([header, ...rows]));
      toast(`${what} copied as CSV.`);
    } catch {
      toast('Could not copy — your browser refused the clipboard.', { kind: 'error' });
    }
  }
  return <button type="button" className="fs-btn neutral sm" onClick={copy}>Copy as CSV</button>;
}

const pct = (n: number, of: number) => (of > 0 ? Math.round((n / of) * 100) : 0);
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** The width every chart has always drawn at, and still does wherever it fits. */
const DESKTOP_W = 640;

/**
 * The width a chart draws its viewBox at (F10). On a desktop window it is
 * 640, exactly as it always was — even where a two-up card grid makes the
 * box narrower, so a 1280px screen renders what it rendered before. Under
 * 900px it is the box's own width (capped at 640), so 12px text stays 12px
 * on a phone instead of being scaled down with the drawing. Without a
 * ResizeObserver (jsdom) the answer is 640.
 */
function useChartWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const narrow = useMediaQuery(NARROW);
  const [measured, setMeasured] = useState(DESKTOP_W);
  useEffect(() => {
    const el = ref.current;
    if (!narrow || !el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const box = Math.floor(el.getBoundingClientRect().width);
      setMeasured(box > 0 ? Math.min(DESKTOP_W, box) : DESKTOP_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [narrow]);
  return [ref, narrow ? measured : DESKTOP_W];
}

export interface BarDatum { label: string; value: number; muted?: boolean }

/**
 * Horizontal bars, one per choice, in the order the form lists them.
 *
 * `total` is what a percentage is of — the answers, for a single choice; the
 * respondents, for a multiple choice, where the bars need not add up.
 */
export function BarChart({ data, total, title }: { data: BarDatum[]; total: number; title: string }) {
  const [box, width] = useChartWidth();
  const rowH = 26;
  // The label and count columns are the desktop's 190 and 90 at 640; in a
  // narrower box they take the same share of it, so the bars keep some room.
  const full = width >= DESKTOP_W;
  const labelW = full ? 190 : Math.round(width * 0.3);
  const countW = full ? 90 : Math.round(width * 0.17);
  const labelChars = full ? 28 : Math.max(10, Math.round(width / 22));
  const height = Math.max(rowH, data.length * rowH);
  const barW = width - labelW - countW;
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div ref={box} style={{ maxWidth: DESKTOP_W }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ maxWidth: width, height: 'auto', display: 'block', fontFamily: 'var(--font-ui, system-ui)' }}
        role="img"
        aria-label={title}
      >
        {data.map((d, i) => {
          const y = i * rowH;
          const w = Math.max(d.value > 0 ? 4 : 0, (d.value / max) * barW);
          return (
            <g key={i}>
              <title>{`${d.label}: ${d.value} (${pct(d.value, total)}%)`}</title>
              <text x={labelW - 10} y={y + rowH / 2 + 4} textAnchor="end" fontSize={12.5} fill={d.muted ? MUTED : INK}>
                {clip(d.label, labelChars)}
              </text>
              <rect x={labelW} y={y + 6} width={barW} height={rowH - 12} rx={4} fill={TRACK} />
              <rect x={labelW} y={y + 6} width={w} height={rowH - 12} rx={4} fill={d.muted ? OTHER_COLOUR : ACCENT} />
              <text x={labelW + barW + 10} y={y + rowH / 2 + 4} fontSize={12} fill={MUTED}>
                {d.value.toLocaleString()} · {pct(d.value, total)}%
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export interface SliceDatum { label: string; value: number; other?: boolean }

/** A part-of-a-whole with a few parts. The legend beside it names every slice. */
export function Donut({ data, title }: { data: SliceDatum[]; title: string }) {
  const total = data.reduce((n, d) => n + d.value, 0);
  const r = 44;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const colourOf = (d: SliceDatum, i: number) => (d.other ? OTHER_COLOUR : SLICE_COLOURS[Math.min(i, SLICE_COLOURS.length - 1)]);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <svg viewBox="0 0 120 120" width={120} height={120} role="img" aria-label={title} style={{ flex: 'none' }}>
        <circle cx={60} cy={60} r={r} fill="none" stroke={TRACK} strokeWidth={16} />
        {total > 0 && data.map((d, i) => {
          const len = (d.value / total) * c;
          // A hairline of surface between slices so two neighbours never merge.
          const gap = data.filter((x) => x.value > 0).length > 1 ? 1.5 : 0;
          const el = (
            <circle
              key={i}
              cx={60} cy={60} r={r} fill="none"
              stroke={colourOf(d, i)} strokeWidth={16}
              strokeDasharray={`${Math.max(0, len - gap)} ${c - Math.max(0, len - gap)}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 60 60)"
            >
              <title>{`${d.label}: ${d.value} (${pct(d.value, total)}%)`}</title>
            </circle>
          );
          offset += len;
          return el;
        })}
        <text x={60} y={57} textAnchor="middle" fontSize={20} fontWeight={700} fill={INK}>{total.toLocaleString()}</text>
        <text x={60} y={73} textAnchor="middle" fontSize={10} fill={MUTED}>answers</text>
      </svg>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6, fontSize: 13, color: INK }}>
        {data.map((d, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: colourOf(d, i), flex: 'none' }} />
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
            <span style={{ color: MUTED, marginLeft: 'auto', paddingLeft: 12, whiteSpace: 'nowrap' }}>
              {d.value.toLocaleString()} · {pct(d.value, total)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface ColumnDatum { label: string; value: number }

/** Columns over an ordered range — a number's buckets, a rating's steps. */
export function Histogram({ data, title }: { data: ColumnDatum[]; title: string }) {
  const [box, width] = useChartWidth();
  const height = 180;
  const pad = { top: 18, right: 8, bottom: 26, left: 8 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...data.map((d) => d.value));
  const slot = data.length ? plotW / data.length : plotW;
  const barW = Math.max(4, slot - 6);
  // Label every column when there is room, else the first, the last and a few between.
  const labelEvery = data.length <= 12 ? 1 : Math.ceil(data.length / 8);
  return (
    <div ref={box} style={{ maxWidth: DESKTOP_W }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ maxWidth: width, height: 'auto', display: 'block', fontFamily: 'var(--font-ui, system-ui)' }}
        role="img"
        aria-label={title}
      >
        <line x1={pad.left} x2={width - pad.right} y1={pad.top + plotH} y2={pad.top + plotH} stroke={GRID} />
        {data.map((d, i) => {
          const h = d.value > 0 ? Math.max(3, (d.value / max) * plotH) : 0;
          const x = pad.left + i * slot + (slot - barW) / 2;
          const y = pad.top + plotH - h;
          const showLabel = tickLabel(i, data.length, labelEvery, width);
          return (
            <g key={i}>
              <title>{`${d.label}: ${d.value}`}</title>
              <rect x={x} y={pad.top} width={barW} height={plotH} fill="transparent" />
              {h > 0 && <rect x={x} y={y} width={barW} height={h} rx={4} fill={ACCENT} />}
              {d.value > 0 && <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize={11} fill={MUTED}>{d.value.toLocaleString()}</text>}
              {showLabel && (
                <text x={x + barW / 2} y={height - 8} textAnchor="middle" fontSize={11} fill={MUTED}>{clip(d.label, 12)}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Which ticks get a label: under 400px only the first, the middle and the last. */
function tickLabel(i: number, n: number, every: number, width: number): boolean {
  if (width < 400) return i === 0 || i === n - 1 || i === Math.floor((n - 1) / 2);
  return i % every === 0 || i === n - 1;
}

export interface PointDatum { label: string; value: number }

/** A count per day as one line; dots mark the days and carry the hover. */
export function LineChart({ data, title }: { data: PointDatum[]; title: string }) {
  const [box, width] = useChartWidth();
  const height = 180;
  const pad = { top: 14, right: 14, bottom: 26, left: 14 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...data.map((d) => d.value));
  const xOf = (i: number) => (data.length > 1 ? pad.left + (i / (data.length - 1)) * plotW : pad.left + plotW / 2);
  const yOf = (v: number) => pad.top + plotH - (v / max) * plotH;
  const points = data.map((d, i) => `${xOf(i)},${yOf(d.value)}`).join(' ');
  const labelEvery = Math.max(1, Math.ceil(data.length / 8));
  return (
    <div ref={box} style={{ maxWidth: DESKTOP_W }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ maxWidth: width, height: 'auto', display: 'block', fontFamily: 'var(--font-ui, system-ui)' }}
        role="img"
        aria-label={title}
      >
        <line x1={pad.left} x2={width - pad.right} y1={pad.top + plotH} y2={pad.top + plotH} stroke={GRID} />
        <line x1={pad.left} x2={width - pad.right} y1={pad.top} y2={pad.top} stroke={GRID} strokeDasharray="3 4" />
        <text x={pad.left} y={pad.top - 4} fontSize={10} fill={MUTED}>{max.toLocaleString()}</text>
        {data.length > 1 && <polyline points={points} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
        {data.map((d, i) => (
          <g key={i}>
            <title>{`${d.label}: ${d.value}`}</title>
            <circle cx={xOf(i)} cy={yOf(d.value)} r={8} fill="transparent" />
            <circle cx={xOf(i)} cy={yOf(d.value)} r={d.value > 0 ? 3.5 : 2} fill={d.value > 0 ? ACCENT : GRID} stroke="var(--surface, #fff)" strokeWidth={1.5} />
            {tickLabel(i, data.length, labelEvery, width) && (
              <text x={xOf(i)} y={height - 8} textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'} fontSize={11} fill={MUTED}>
                {d.label}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

/**
 * One chart with its table twin and its copy button, under one label.
 *
 * `rows` are the numbers the chart draws — the same ones the screen reader
 * gets and the clipboard gets, so the three can never disagree.
 */
export function Figure({ title, header, rows, children }: {
  title: string; header: string[]; rows: CsvRow[]; children: React.ReactNode;
}) {
  const id = useId();
  /*
    F5': the numbers open by default on the narrowest screens. A 12.5px label
    column in a 280px chart is legible but tight, and the table beneath it is
    the same rows exactly — so the phone starts with both. Read once, on
    mount: a desktop (and jsdom, which has no matchMedia) starts closed, as
    it always has, and the toggle still owns it from then on.
  */
  const [showTable, setShowTable] = useState(() => matchesNow('(max-width: 480px)'));
  return (
    <figure style={{ margin: 0 }} aria-labelledby={id}>
      <figcaption id={id} className="fs-sr-only">{title}</figcaption>
      {children}
      <HiddenTable caption={title} header={header} rows={rows} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
        <CopyCsvButton header={header} rows={rows} what={title} />
        <button type="button" className="fs-btn neutral sm" onClick={() => setShowTable((v) => !v)} aria-expanded={showTable}>
          {showTable ? 'Hide table' : 'Show table'}
        </button>
      </div>
      {showTable && (
        <div className="fs-table-scroll" style={{ marginTop: 10 }}>
          <table className="fs-table" style={{ maxWidth: DESKTOP_W }}>
            <thead><tr>{header.map((h, i) => <th key={i} className={i > 0 ? 'num' : undefined}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>{r.map((c, j) => <td key={j} className={j > 0 ? 'num' : undefined}>{c ?? ''}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </figure>
  );
}
